import { zodResolver } from '@hookform/resolvers/zod';
import { CheckCircle2, Eye, EyeOff, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { z } from 'zod';

import { ApiError, authFetch } from '@/api/http-client';
import { AuthShell } from '@/features/auth/components/AuthShell';
import { ROTAS } from '@/shared/lib/rotas';
import { Alert, AlertDescription } from '@/shared/ui/alert';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';

/**
 * ⚠️ O mínimo de 8 é o mesmo do `minPasswordLength` do better-auth.
 *
 * Validar só aqui deixaria o servidor recusar com uma mensagem genérica depois
 * do submit; validar só lá tiraria o feedback enquanto digita. Os dois valem, e
 * o número precisa bater — por isso está escrito no comentário dos dois lados.
 */
const schema = z
  .object({
    novaSenha: z.string().min(8, 'A senha precisa de ao menos 8 caracteres'),
    confirmacao: z.string(),
  })
  .refine((v) => v.novaSenha === v.confirmacao, {
    path: ['confirmacao'],
    message: 'As senhas não conferem',
  });

type Formulario = z.infer<typeof schema>;

/**
 * Redefinição de senha.
 *
 * O token vem na query, montado pelo `sendResetPassword` do backend apontando
 * para cá — não pelo `url` que o better-auth oferece, que deixaria o cliente
 * escolher o destino do link do e-mail.
 */
export function RedefinirSenhaPage() {
  const [busca] = useSearchParams();
  const navigate = useNavigate();
  const token = busca.get('token');

  const [verSenha, setVerSenha] = useState(false);
  const [erro, setErro] = useState<string | undefined>();
  const [pronto, setPronto] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<Formulario>({ resolver: zodResolver(schema) });

  // Sem token não há o que fazer: o link foi truncado no cliente de e-mail, ou
  // alguém abriu a URL na mão. Dizer isso é melhor que mostrar um formulário
  // que vai falhar no submit.
  if (!token) {
    return (
      <AuthShell descricao="Redefinir senha">
        <div className="space-y-4">
          <Alert variant="destructive">
            <AlertDescription className="text-sm">
              Link inválido ou incompleto. Peça um novo e-mail de redefinição.
            </AlertDescription>
          </Alert>
          <Button asChild variant="outline" className="w-full">
            <Link to={ROTAS.esqueciSenha}>Pedir novo link</Link>
          </Button>
        </div>
      </AuthShell>
    );
  }

  if (pronto) {
    return (
      <AuthShell descricao="Senha redefinida">
        <div className="space-y-4">
          <Alert className="bg-emerald-500/10 border-emerald-500/30">
            <CheckCircle2 className="h-4 w-4" />
            <AlertDescription className="text-sm">
              Senha alterada. Você já pode entrar com a nova senha.
            </AlertDescription>
          </Alert>
          <Button asChild className="w-full bg-brand text-brand-foreground hover:bg-brand/90">
            <Link to={ROTAS.login}>Ir para o login</Link>
          </Button>
        </div>
      </AuthShell>
    );
  }

  async function aoEnviar(valores: Formulario): Promise<void> {
    setErro(undefined);

    try {
      await authFetch('/reset-password', { newPassword: valores.novaSenha, token });
      setPronto(true);
    } catch (falha) {
      // Token expirado e token já usado chegam como 400 do better-auth. A
      // mensagem precisa oferecer a saída, não só constatar o problema.
      setErro(
        falha instanceof ApiError && falha.status === 400
          ? 'Este link expirou ou já foi usado. Peça um novo e-mail de redefinição.'
          : 'Não foi possível redefinir a senha. Tente novamente.',
      );
    }
  }

  return (
    <AuthShell descricao="Escolha uma nova senha">
      <form onSubmit={(e) => void handleSubmit(aoEnviar)(e)} className="space-y-4">
        {erro && (
          <Alert variant="destructive">
            <AlertDescription className="text-sm">{erro}</AlertDescription>
          </Alert>
        )}

        <div className="space-y-2">
          <Label htmlFor="novaSenha" className="text-brand font-medium">
            Nova senha
          </Label>
          <div className="relative">
            <Input
              id="novaSenha"
              type={verSenha ? 'text' : 'password'}
              autoComplete="new-password"
              className="pr-10"
              {...register('novaSenha')}
            />
            <button
              type="button"
              onClick={() => setVerSenha((v) => !v)}
              aria-label={verSenha ? 'Ocultar senha' : 'Mostrar senha'}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-brand/50 hover:text-brand transition-colors"
            >
              {verSenha ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          {errors.novaSenha && (
            <p className="text-xs text-destructive">{errors.novaSenha.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="confirmacao" className="text-brand font-medium">
            Confirme a nova senha
          </Label>
          <Input
            id="confirmacao"
            type={verSenha ? 'text' : 'password'}
            autoComplete="new-password"
            {...register('confirmacao')}
          />
          {errors.confirmacao && (
            <p className="text-xs text-destructive">{errors.confirmacao.message}</p>
          )}
        </div>

        <Button
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-brand text-brand-foreground hover:bg-brand/90"
        >
          {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Redefinir senha
        </Button>

        <div className="text-center">
          <button
            type="button"
            onClick={() => navigate(ROTAS.login)}
            className="text-sm text-brand hover:underline"
          >
            Voltar para o login
          </button>
        </div>
      </form>
    </AuthShell>
  );
}
